import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────
// ASSETS
//   src/assets/logos/hero1.png  (wide convoy / escort truck photo)
// ─────────────────────────────────────────────────────────────────────
import heroTruck from '../assets/logos/hero1.png';

// Map data for the HomeStateMap
import { STATE_PATHS, STATE_LABELS, STATES_DATA } from '../Training/TrainingHub';

// ─────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — enterprise palette: NAVY + BLUE + SLATE
// ─────────────────────────────────────────────────────────────────────
const DARK = {
  bg:        "#0b1a33",
  surface:   "#12264a",
  surface2:  "#183158",
  border:    "rgba(255,255,255,0.10)",
  borderSoft:"rgba(255,255,255,0.06)",
  accent:    "#3b82f6",
  accentL:   "#60a5fa",
  accentBg:  "rgba(59,130,246,0.14)",
  green:     "#34d399",
  greenBg:   "rgba(52,211,153,0.10)",
  text:      "#f1f5f9",
  textMid:   "rgba(241,245,249,0.76)",
  textSoft:  "rgba(241,245,249,0.52)",
  gridLine:  "rgba(255,255,255,0.035)",
  glow:      "rgba(59,130,246,0.20)",
  shadow:    "0 30px 70px rgba(0,0,0,0.35)",
  cardShadow:"0 1px 0 rgba(255,255,255,0.04) inset",
  glass:     "rgba(18,38,74,0.88)",
  gold:      "#f5b700",
  bgA:       (a) => `rgba(11,26,51,${a})`,
};

const LIGHT = {
  bg:        "#f8fafc",
  surface:   "#ffffff",
  surface2:  "#f1f5f9",
  border:    "#e2e8f0",
  borderSoft:"#f1f5f9",
  accent:    "#2563eb",
  accentL:   "#3b82f6",
  accentBg:  "rgba(37,99,235,0.08)",
  green:     "#16a34a",
  greenBg:   "rgba(22,163,74,0.08)",
  text:      "#0f172a",
  textMid:   "#475569",
  textSoft:  "#64748b",
  gridLine:  "rgba(15,23,42,0.04)",
  glow:      "rgba(37,99,235,0.10)",
  shadow:    "0 30px 70px rgba(15,23,42,0.14)",
  cardShadow:"0 1px 2px rgba(15,23,42,0.04)",
  glass:     "rgba(255,255,255,0.94)",
  gold:      "#f5b700",
  bgA:       (a) => `rgba(248,250,252,${a})`,
};

const NAVY = "#0b1a33";

// ─────────────────────────────────────────────────────────────────────
// ICONS — crisp line icons replace emoji for an enterprise feel
// ─────────────────────────────────────────────────────────────────────
const ICONS = {
  arrow:     <path d="M5 12h14M13 6l6 6-6 6" />,
  check:     <path d="M5 12.5l4.5 4.5L19 7.5" />,
  truck:     <><path d="M2 6h12v10H2z" /><path d="M14 9h4.5l3.5 3.5V16h-8z" /><circle cx="6" cy="17.5" r="1.8" /><circle cx="17.5" cy="17.5" r="1.8" /></>,
  building:  <><path d="M4 21V4h11v17" /><path d="M15 9h5v12" /><path d="M8 8h3M8 12h3M8 16h3M3 21h18" /></>,
  clipboard: <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4.5V3h6v1.5M9 10h6M9 14h6M9 18h3" /></>,
  shield:    <><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M8.5 12l2.5 2.5L15.5 10" /></>,
  radar:     <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.3" /></>,
  book:      <path d="M4 19V5a2 2 0 0 1 2-2h14v14H6a2 2 0 0 0-2 2 2 2 0 0 0 2 2h14" />,
  cart:      <><path d="M3 4h2.5l2.2 11h10.6L20.5 8H7" /><circle cx="9.5" cy="19.5" r="1.4" /><circle cx="17" cy="19.5" r="1.4" /></>,
  chat:      <><path d="M4 5h16v11H9l-5 4z" /><path d="M8 9.5h8M8 12.5h5" /></>,
  layers:    <><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></>,
  map:       <><path d="M9 4L3 6.5V20l6-2.5 6 2.5 6-2.5V4l-6 2.5z" /><path d="M9 4v13.5M15 6.5V20" /></>,
  zap:       <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  pin:       <><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></>,
  users:     <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14.2c2.9.3 5 2.6 5 5.8" /></>,
  file:      <><path d="M6 3h8l5 5v13H6z" /><path d="M14 3v5h5M9 13h7M9 17h7" /></>,
};

function Icon({ name, size = 20, stroke = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ flexShrink: 0, display: "block" }}>
      {ICONS[name]}
    </svg>
  );
}

// Tinted square that holds an icon
function IconTile({ T, name, size = 48, iconSize = 22 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 12, flexShrink: 0,
      background: T.accentBg, border: `1px solid ${T.accent}33`,
      color: T.accent === "#2563eb" ? T.accent : T.accentL,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <Icon name={name} size={iconSize} />
    </div>
  );
}

// Partner program content — honest scope instead of placeholder logos
const PARTNER_CATEGORIES = ["Carriers", "Insurance", "Escort Equipment", "Fleet Vehicles", "Software & Tech", "Business Services"];
const PARTNER_BENEFITS = [
  ["Priority visibility", "Featured placement in front of verified drivers and companies."],
  ["Direct network access", "Reach escort professionals who are ID, cert and insurance checked."],
  ["Dedicated account manager", "One point of contact from onboarding onward."],
];
const PARTNER_STEPS = [["01", "Apply"], ["02", "We review"], ["03", "Launch"]];

// ─────────────────────────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────────────
function Card({ T, children, style, to }) {
  const cardStyle = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    overflow: "hidden",
    textDecoration: "none",
    color: "inherit",
    display: "block",
    boxShadow: T.cardShadow,
    transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
    ...style,
  };
  if (to) return <Link to={to} className="pl-card" style={cardStyle}>{children}</Link>;
  return <div className="pl-card" style={cardStyle}>{children}</div>;
}

function PrimaryBtn({ T, children, onClick, style, to }) {
  const btnStyle = {
    background: "linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)",
    border: "none", borderRadius: 10,
    padding: "clamp(13px, 2vw, 15px) clamp(22px, 4vw, 30px)",
    fontSize: "clamp(14px, 1.5vw, 16px)", fontWeight: 700, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    textDecoration: "none", cursor: "pointer", textAlign: "center",
    flex: "1 1 auto", minWidth: "max-content",
    boxShadow: "0 10px 24px rgba(37,99,235,0.28)",
    ...style,
  };
  if (to) return <Link to={to} className="pl-btn" style={btnStyle}>{children}</Link>;
  return <button className="pl-btn" onClick={onClick} style={btnStyle}>{children}</button>;
}

function SecondaryBtn({ T, children, onClick, style, to, onNavy }) {
  const btnStyle = {
    background: onNavy ? "transparent" : T.surface,
    border: `1.5px solid ${onNavy ? "rgba(255,255,255,0.35)" : T.border}`,
    borderRadius: 10,
    padding: "clamp(13px, 2vw, 15px) clamp(22px, 4vw, 30px)",
    fontSize: "clamp(14px, 1.5vw, 16px)", fontWeight: 700,
    color: onNavy ? "#f1f5f9" : T.text,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    textDecoration: "none", cursor: "pointer", textAlign: "center",
    flex: "1 1 auto", minWidth: "max-content",
    ...style,
  };
  if (to) return <Link to={to} className="pl-btn-ghost" style={btnStyle}>{children}</Link>;
  return <button className="pl-btn-ghost" onClick={onClick} style={btnStyle}>{children}</button>;
}

function Eyebrow({ T, isDarkMode, children, center }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: center ? "center" : "flex-start", gap: 10,
      fontSize: 13, fontWeight: 700, letterSpacing: ".02em",
      color: isDarkMode ? T.accentL : T.accent, marginBottom: 12,
    }}>
      <span style={{ width: 24, height: 1.5, background: "currentColor", opacity: 0.7 }} />
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HOME STATE MAP
// ─────────────────────────────────────────────────────────────────────
function HomeStateMap({ T, isDarkMode }) {
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const wrapRef = React.useRef(null);
  const navigate = useNavigate();
  const D = isDarkMode;

  const oceanBg = D ? "#081427" : "#eef5fb";
  const tooltipBg = D ? "#12264a" : "#ffffff";
  const tooltipText = D ? "#f1f5f9" : "#0f172a";
  const tooltipSub = D ? "rgba(241,245,249,0.65)" : "#475569";
  const noStateFill = D ? "#22344a" : "#dbe6ee";
  const noStateStroke = D ? "#30465f" : "#c3d3de";
  const stateStroke = D ? "#081427" : "#ffffff";
  const legendText = D ? "rgba(241,245,249,0.65)" : "#475569";

  const getCategory = (code) => {
    const s = STATES_DATA[code];
    if (!s) return null;
    if (s.inNetwork)   return { color: "#0891b2", label: "13-State Network" };
    if (s.independent) return { color: "#d97706", label: "Independent Certification" };
    return { color: "#64748b", label: "No Certification Required" };
  };

  const getColor = (code) => {
    const cat = getCategory(code);
    if (!cat) return { fill: noStateFill, stroke: noStateStroke };
    return { fill: `${cat.color}b3`, stroke: cat.color };
  };

  const handleMove = (e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top, w: rect.width, h: rect.height });
  };

  const h = hovered && STATES_DATA[hovered];
  const hCat = hovered && getCategory(hovered);

  const TT_W = 250;
  let ttLeft = mousePos.x + 18;
  let ttTop = mousePos.y + 18;
  if (mousePos.w && ttLeft + TT_W > mousePos.w) ttLeft = mousePos.x - TT_W - 18;
  if (mousePos.h && ttTop + 150 > mousePos.h) ttTop = Math.max(10, mousePos.y - 150);

  return (
    <div ref={wrapRef} style={{ position: "relative" }} onMouseMove={handleMove}>
      <span style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
        Certification coverage map. Click any state to see its requirements and available courses.
      </span>
      <svg viewBox="0 0 959 593" preserveAspectRatio="xMidYMid meet" role="img" aria-label="US map — click a state to see certification requirements and courses" style={{ width: "100%", height: "auto", aspectRatio: "959 / 593", display: "block", borderRadius: 18, background: oceanBg, border: `1px solid ${T.border}`, boxShadow: T.cardShadow, transition: "background .3s" }}>
        {Object.entries(STATE_PATHS).map(([code, path]) => {
          const { fill, stroke } = getColor(code);
          const isHov = hovered === code;
          const clickable = !!STATES_DATA[code];
          return (
            <path key={code} d={path} fill={isHov ? stroke : fill} stroke={stateStroke} strokeWidth={isHov ? 1.6 : 1}
              style={{
                cursor: clickable ? "pointer" : "default",
                transition: "fill .12s, filter .12s",
                filter: isHov ? "drop-shadow(0 3px 8px rgba(13,31,60,0.35))" : "none",
              }}
              onMouseEnter={() => clickable && setHovered(code)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => clickable && navigate(`/training?state=${code}`)} />
          );
        })}
        {STATE_LABELS.map(([code, x, y]) => {
          const s = STATES_DATA[code];
          if (!s) return null;
          return (
            <text key={code} x={x} y={y} textAnchor="middle" fontSize={11} fontWeight="700"
              fontFamily="Inter,sans-serif" fill="#fff" pointerEvents="none"
              style={{ userSelect: "none", opacity: 0.95, textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
              {code}
            </text>
          );
        })}
      </svg>

      {h && hCat && (
        <div style={{
          position: "absolute", left: ttLeft, top: ttTop, background: tooltipBg, border: `1.5px solid ${hCat.color}55`,
          borderRadius: 14, padding: "14px 18px", width: TT_W, boxShadow: "0 12px 32px rgba(13,31,60,0.22)",
          pointerEvents: "none", transition: "background .3s", zIndex: 5,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
            <span style={{ fontSize: 20 }}>{h.flag}</span>
            <div style={{ fontWeight: 800, fontSize: 15, color: tooltipText }}>{h.name}</div>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, background: `${hCat.color}18`, color: hCat.color, padding: "4px 10px", borderRadius: 99, border: `1px solid ${hCat.color}30`, marginBottom: 9 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: hCat.color, flexShrink: 0 }} />
            {hCat.label}
          </div>
          <div style={{ fontSize: 12.5, color: tooltipSub, lineHeight: 1.6, marginBottom: 8 }}>{h.note}</div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: hCat.color }}>Click to see courses →</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 14 }}>
        {[
          { color: "#0891b2b3", label: "13-State Reciprocity Network" },
          { color: "#d97706b3", label: "Independent certification required" },
          { color: "#64748bb3", label: "No certification required" },
        ].map(l => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: legendText }}>
            <div style={{ width: 13, height: 13, borderRadius: 3, background: l.color, flexShrink: 0 }} />
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PARTNER PROGRAM — adapts to light and dark mode
// ─────────────────────────────────────────────────────────────────────
function PartnerSection({ T, isDarkMode }) {
  const D = isDarkMode;
  const P = D ? {
    panelBg:     `linear-gradient(135deg, ${NAVY} 0%, #15315a 55%, #0f2547 100%)`,
    panelBorder: "rgba(147,197,253,0.18)",
    panelShadow: "0 30px 70px rgba(0,0,0,0.35)",
    gridLine:    "rgba(255,255,255,0.035)",
    glowA:       "rgba(59,130,246,0.22)",
    glowB:       "rgba(96,165,250,0.16)",
    eyebrow:     "#93c5fd",
    heading:     "#f8fafc",
    body:        "rgba(241,245,249,0.74)",
    muted:       "rgba(241,245,249,0.50)",
    chipBg:      "rgba(255,255,255,0.05)",
    chipBorder:  "rgba(255,255,255,0.12)",
    chipText:    "rgba(241,245,249,0.88)",
    glassBg:     "rgba(255,255,255,0.06)",
    glassBorder: "rgba(255,255,255,0.14)",
    glassShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    divider:     "rgba(255,255,255,0.10)",
    descText:    "rgba(241,245,249,0.62)",
    stepNum:     "#60a5fa",
    stepText:    "rgba(241,245,249,0.82)",
    check:       "#34d399",
    checkBg:     "rgba(52,211,153,0.14)",
  } : {
    panelBg:     "linear-gradient(135deg, #ffffff 0%, #f6f9ff 55%, #eef4ff 100%)",
    panelBorder: "#dbe5f3",
    panelShadow: "0 24px 60px rgba(15,23,42,0.08)",
    gridLine:    "rgba(37,99,235,0.05)",
    glowA:       "rgba(59,130,246,0.12)",
    glowB:       "rgba(96,165,250,0.10)",
    eyebrow:     "#2563eb",
    heading:     "#0f172a",
    body:        "#475569",
    muted:       "#64748b",
    chipBg:      "#ffffff",
    chipBorder:  "#e2e8f0",
    chipText:    "#334155",
    glassBg:     "#ffffff",
    glassBorder: "#e2e8f0",
    glassShadow: "0 20px 50px rgba(15,23,42,0.08)",
    divider:     "#e2e8f0",
    descText:    "#64748b",
    stepNum:     "#2563eb",
    stepText:    "#334155",
    check:       "#16a34a",
    checkBg:     "rgba(22,163,74,0.10)",
  };

  return (
    <section style={{ background: T.bg, padding: "clamp(30px, 5vw, 64px) clamp(20px, 4vw, 40px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{
          position: "relative", overflow: "hidden", isolation: "isolate",
          background: P.panelBg,
          borderRadius: 28,
          border: `1px solid ${P.panelBorder}`,
          boxShadow: P.panelShadow,
          padding: "clamp(32px, 6vw, 64px)",
          transition: "background .3s, border-color .3s",
        }}>
          <div aria-hidden="true" style={{
            position: "absolute", inset: 0, zIndex: -1,
            backgroundImage: `linear-gradient(${P.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${P.gridLine} 1px, transparent 1px)`,
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse 80% 70% at 30% 30%, black 30%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 30% 30%, black 30%, transparent 100%)",
          }} />
          <div aria-hidden="true" style={{ position: "absolute", top: "-30%", left: "-10%", width: 520, height: 520, borderRadius: "50%", zIndex: -1, background: `radial-gradient(circle, ${P.glowA} 0%, transparent 70%)` }} />
          <div aria-hidden="true" style={{ position: "absolute", bottom: "-35%", right: "-5%", width: 480, height: 480, borderRadius: "50%", zIndex: -1, background: `radial-gradient(circle, ${P.glowB} 0%, transparent 70%)` }} />

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
            gap: "clamp(32px, 5vw, 64px)",
            alignItems: "center",
          }}>
            {/* Left: invitation */}
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 700, letterSpacing: ".02em", color: P.eyebrow, marginBottom: 18 }}>
                <span style={{ width: 28, height: 1.5, background: "currentColor", opacity: 0.7 }} />
                Partner Program
              </div>

              <h2 style={{
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: "clamp(28px, 3.6vw + 8px, 44px)", fontWeight: 900,
                color: P.heading, lineHeight: 1.12, letterSpacing: "-0.02em", marginBottom: 18,
              }}>
                Grow alongside America's verified escort network
              </h2>

              <p style={{ fontSize: "clamp(15px, 1.8vw, 17px)", color: P.body, lineHeight: 1.75, maxWidth: 500, marginBottom: 28 }}>
                We work with companies that keep oversize loads moving safely. If your business serves
                escort drivers or the carriers who hire them, let's talk about building something together.
              </p>

              <div style={{ fontSize: 13, fontWeight: 600, color: P.muted, marginBottom: 12 }}>
                Who we partner with
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {PARTNER_CATEGORIES.map((c) => (
                  <span key={c} style={{
                    fontSize: 13, fontWeight: 600, color: P.chipText,
                    padding: "7px 14px", borderRadius: 99,
                    background: P.chipBg, border: `1px solid ${P.chipBorder}`,
                  }}>{c}</span>
                ))}
              </div>
            </div>

            {/* Right: application panel */}
            <div style={{
              background: P.glassBg,
              backdropFilter: D ? "blur(12px)" : "none", WebkitBackdropFilter: D ? "blur(12px)" : "none",
              border: `1px solid ${P.glassBorder}`,
              borderRadius: 22,
              padding: "clamp(24px, 4vw, 36px)",
              boxShadow: P.glassShadow,
            }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 19, fontWeight: 800, color: P.heading, marginBottom: 18 }}>
                What partners receive
              </div>

              <div style={{ display: "grid", gap: 14, marginBottom: 28 }}>
                {PARTNER_BENEFITS.map(([title, desc]) => (
                  <div key={title} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{
                      flexShrink: 0, width: 24, height: 24, borderRadius: "50%", marginTop: 1,
                      background: P.checkBg, border: `1px solid ${P.check}66`, color: P.check,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon name="check" size={12} stroke={3.2} />
                    </span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: P.heading }}>{title}</div>
                      <div style={{ fontSize: 13.5, color: P.descText, lineHeight: 1.55, marginTop: 2 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10,
                paddingTop: 22, marginBottom: 26, borderTop: `1px solid ${P.divider}`,
              }}>
                {PARTNER_STEPS.map(([n, label]) => (
                  <div key={n} style={{ textAlign: "center" }}>
                    <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, fontWeight: 800, color: P.stepNum, letterSpacing: ".06em" }}>{n}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: P.stepText, marginTop: 4 }}>{label}</div>
                  </div>
                ))}
              </div>

              <Link to="/become-partner" className="pl-partner-cta" style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", boxSizing: "border-box",
                background: "linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)",
                color: "#fff", textDecoration: "none", fontSize: 16, fontWeight: 700,
                padding: "16px 24px", borderRadius: 12,
                boxShadow: "0 10px 26px rgba(37,99,235,0.32)",
              }}>
                Apply to become a partner
                <Icon name="arrow" size={18} stroke={2.4} />
              </Link>

              <div style={{ textAlign: "center", fontSize: 12.5, color: P.muted, marginTop: 14, fontWeight: 500 }}>
                Free to apply, no commitment
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────
export default function HeroPage({ isDarkMode }) {
  const T = isDarkMode ? DARK : LIGHT;
  const navigate = useNavigate();
  const accentText = isDarkMode ? T.accentL : T.accent;

  const handleLoadBoard = (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) navigate("/load-board");
      else navigate("/login?redirect=/load-board&reason=loadboard");
    } catch {
      navigate("/login?redirect=/load-board&reason=loadboard");
    }
  };

  const services = [
    { icon:"clipboard", title:"Live Load Board",          desc:"Real-time oversize load listings across all 50 states. See rates, permit details and route maps, then apply with one tap.", page:"user-dashboard" },
    { icon:"shield",    title:"Verified Driver Network",  desc:"Every escort driver is ID-checked, certification-verified and rated by real companies. No guesswork, no risk.", page:"login" },
    { icon:"radar",     title:"Dispatcher Console",       desc:"Live GPS tracking, escort team management and AI-powered dispatch. Run every job from one screen.", page:"dispatcher-login" },
    { icon:"book",      title:"Training & Certification", desc:"State-by-state certification courses, PPCA exam prep and route planning tools to keep your credentials current.", page:"training" },
    { icon:"cart",      title:"Equipment Marketplace",    desc:"Buy and sell pilot car gear, from trucks and light bars to high-poles, flags and safety equipment.", page:"marketplace" },
    { icon:"chat",      title:"Chat, Invoicing & Pay",    desc:"Message drivers and companies instantly, send invoices, and get paid faster without leaving the platform.", page:"user-dashboard" },
  ];

  const trustPillars = [
    { icon:"shield", title:"Verified, Not Just Listed", desc:"We verify identity, certifications and insurance before any driver goes live. Companies see proof, not promises." },
    { icon:"layers", title:"Everything In One Place",   desc:"Loads, drivers, dispatch, training, gear and payments. No juggling five websites and a Facebook group." },
    { icon:"map",    title:"Coast-to-Coast Coverage",   desc:"Certified escort drivers in all 50 states, with state-specific permit and certification knowledge built in." },
    { icon:"zap",    title:"Faster Jobs, Faster Pay",   desc:"Real-time matching gets loads covered in minutes, and built-in invoicing gets drivers paid without the chase." },
  ];

  const tickerItems = ["Load Board", "Verified Drivers", "Dispatch", "Training", "Marketplace", "Invoicing"];

  return (
    <div style={{ background: T.bg, transition: "background .3s" }}>
      <style>{`
        @keyframes plPulseDot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.5; transform:scale(0.85); } }
        .pl-card:hover { transform: translateY(-3px); border-color: ${T.accent}55 !important; box-shadow: 0 16px 36px ${isDarkMode ? "rgba(0,0,0,0.35)" : "rgba(15,23,42,0.10)"} !important; }
        .pl-btn { transition: filter .2s ease, transform .2s ease; }
        .pl-btn:hover { filter: brightness(1.08); transform: translateY(-1px); }
        .pl-btn-ghost { transition: border-color .2s ease, color .2s ease; }
        .pl-btn-ghost:hover { border-color: ${T.accent} !important; color: ${accentText} !important; }
        .pl-btn:focus-visible, .pl-btn-ghost:focus-visible, .pl-card:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; }
        .pl-partner-cta { transition: transform .2s ease, box-shadow .2s ease, filter .2s ease; }
        .pl-partner-cta:hover { transform: translateY(-2px); filter: brightness(1.08); box-shadow: 0 14px 34px rgba(59,130,246,0.45) !important; }
        .pl-partner-cta:focus-visible { outline: 2px solid #93c5fd; outline-offset: 3px; }
        /* ── HERO (mobile-first) ── */
        .plh-section { position: relative; overflow: hidden; isolation: isolate; }
        .plh-inner { max-width: 1280px; margin: 0 auto; padding-left: clamp(18px, 4vw, 40px); padding-right: clamp(18px, 4vw, 40px); box-sizing: border-box; }
        .plh-top { position: relative; }
        .plh-copy { padding: 40px 0 8px; max-width: 620px; }
        .plh-eyebrow { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; font-size: 11.5px; font-weight: 700; letter-spacing: .2em; text-transform: uppercase; }
        .plh-eyebrow-line { width: 44px; height: 2px; border-radius: 2px; flex-shrink: 0; }
        .plh-h1 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: clamp(34px, 7.5vw, 48px); font-weight: 900; line-height: 1.06; letter-spacing: -0.03em; margin: 0 0 18px; text-wrap: balance; }
        .plh-lead { font-size: 16px; line-height: 1.65; margin: 0 0 26px; max-width: 540px; }
        .plh-actions { display: flex; flex-direction: column; align-items: stretch; gap: 12px; }
        .plh-gold, .plh-company { display: inline-flex; align-items: center; justify-content: center; gap: 10px; text-decoration: none; font-size: 16px; font-weight: 800; padding: 16px 26px; border-radius: 10px; transition: transform .2s ease, filter .2s ease; }
        .plh-gold { color: #0f172a; box-shadow: 0 10px 24px rgba(245,183,0,0.32); }
        .plh-company { color: #ffffff; ${isDarkMode
          ? "background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%); box-shadow: 0 10px 24px rgba(37,99,235,0.30);"
          : "background: linear-gradient(180deg, #1e3a6e 0%, #0b1a33 100%); box-shadow: 0 10px 24px rgba(11,26,51,0.25);"} }
        .plh-gold:hover, .plh-company:hover { transform: translateY(-2px); filter: brightness(1.05); }

        /* No photo on phones and tablets */
        .plh-photo, .plh-mscrim, .plh-tagline { display: none; }

        .plh-float { position: relative; z-index: 3; display: flex; align-items: center; gap: 12px; text-align: left; font: inherit; cursor: pointer;
          width: 100%; max-width: 420px; box-sizing: border-box; margin: 20px 0 0;
          padding: 12px 16px; border-radius: 16px; backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          box-shadow: 0 12px 30px rgba(15,23,42,0.10); transition: transform .2s ease; }
        .plh-float:hover { transform: translateY(-2px); }
        .plh-float-icon { width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .plh-top > .plh-float { margin-left: clamp(18px, 4vw, 40px); width: calc(100% - 2 * clamp(18px, 4vw, 40px)); }

        .plh-bottom { position: relative; z-index: 2; }
        .plh-features { display: grid; grid-template-columns: 1fr; gap: 12px; margin-top: 28px; }
        .plh-feature { display: flex; align-items: flex-start; gap: 14px; border-radius: 16px; padding: 16px 18px; box-sizing: border-box; }
        .plh-feature-icon { width: 46px; height: 46px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }

        .plh-stats { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px 16px; margin-top: 32px; padding: 26px 0 34px; }
        .plh-stat { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .plh-stat-value { display: block; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; }
        .plh-map-tag { grid-column: 1 / -1; display: flex; align-items: center; gap: 18px; }
        .plh-map { width: 110px; height: auto; flex-shrink: 0; }

        .plh-gold:focus-visible, .plh-company:focus-visible, .plh-float:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 3px; }

        @media (max-width: 399px) {
          .plh-eyebrow { letter-spacing: .12em; font-size: 10.5px; }
          .plh-eyebrow-line { display: none; }
        }

        @media (min-width: 600px) {
          .plh-copy { padding-top: 56px; }
          .plh-actions { flex-direction: row; align-items: center; flex-wrap: wrap; gap: 14px; }
          .plh-lead { font-size: 17px; }
          .plh-features { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
          .plh-stats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .plh-top > .plh-float { width: auto; }
        }

        /* Desktop: full-bleed photo behind the hero, no frame or border */
        @media (min-width: 1024px) {
          .plh-top { display: flex; flex-direction: column; justify-content: center; min-height: clamp(560px, 46vw, 700px); background: ${NAVY}; }
          .plh-top > .plh-inner { width: 100%; position: relative; z-index: 2; }
          .plh-copy { max-width: 580px; padding: 72px 0 128px; }
          .plh-h1 { font-size: clamp(42px, 4vw, 64px); color: #ffffff !important; text-shadow: 0 2px 24px rgba(0,0,0,0.25); }
          .plh-accent { color: #93c5fd !important; }
          .plh-eyebrow { color: #ffffff !important; }
          .plh-lead { font-size: 18px; color: rgba(241,245,249,0.86) !important; }
          .plh-gold, .plh-company { padding: 15px 24px; font-size: 15.5px; }
          .plh-company { background: linear-gradient(180deg, #ffffff 0%, #eef3fb 100%); color: ${NAVY}; box-shadow: 0 10px 24px rgba(0,0,0,0.25); }

          .plh-photo { display: block; position: absolute; inset: 0; z-index: 0; margin: 0; border: 0; border-radius: 0; box-shadow: none; }
          .plh-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: 60% 68%; display: block; }
          .plh-mscrim { display: block; position: absolute; inset: 0; pointer-events: none;
            background:
              linear-gradient(90deg, rgba(11,26,51,0.94) 0%, rgba(11,26,51,0.80) 28%, rgba(11,26,51,0.30) 55%, rgba(11,26,51,0) 72%),
              linear-gradient(180deg, rgba(11,26,51,0.35) 0%, rgba(11,26,51,0) 22%, rgba(11,26,51,0) 70%, rgba(11,26,51,0.55) 100%); }

          .plh-top > .plh-float { position: absolute; z-index: 3; top: 32px; right: clamp(24px, 3vw, 40px); margin: 0; width: auto; max-width: 340px; }
          .plh-bottom { margin-top: -64px; }
          .plh-features { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 0; }
          .plh-feature { padding: 18px; }
          .plh-stats { grid-template-columns: repeat(4, minmax(0, 1fr)) auto; align-items: center; margin-top: 40px; }
          .plh-stat + .plh-stat { border-left: 1px solid ${T.border}; padding-left: 20px; }
          .plh-map-tag { grid-column: auto; padding-left: 20px; }
          .plh-stat-value { font-size: 26px; }
        }

        @media (min-width: 1280px) {
          .plh-map { width: 120px; }
        }

        @media (min-width: 1024px) and (max-width: 1199px) {
          .plh-stats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
          .plh-map-tag { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .pl-card, .pl-btn, .pl-btn-ghost, .pl-partner-cta, .plh-gold, .plh-company, .plh-float { transition: none !important; }
          .plh-gold:hover, .plh-company:hover, .plh-float:hover { transform: none; }
          .pl-card:hover, .pl-btn:hover, .pl-partner-cta:hover { transform: none; }
        }
      `}</style>

      {/* ══════════════════════════ HERO ══════════════════════════
          Desktop (≥1024px): full-bleed photo behind the hero with a navy fade on the left.
          Phones & tablets: no photo, clean text-first layout in the page theme. */}
      <section className="plh-section" style={{ background: T.bg, borderBottom: `1px solid ${T.border}` }}>
        <div className="plh-top">
          {/* Copy */}
          <div className="plh-inner">
            <div className="plh-copy">
              <div className="plh-eyebrow" style={{ color: isDarkMode ? T.accentL : NAVY }}>
                <span>Oversize load escort platform</span>
                <span className="plh-eyebrow-line" style={{ background: T.gold }} />
              </div>

              <h1 className="plh-h1" style={{ color: T.text }}>
                <span style={{ display: "block" }}>Every Escort Service.</span>
                <span style={{ display: "block" }}>One Trusted <span className="plh-accent" style={{ color: accentText }}>Platform.</span></span>
              </h1>

              <p className="plh-lead" style={{ color: T.textMid }}>
                Verified pilot car drivers, a live load board, dispatch, training and payments that keep
                oversize loads moving safely and on schedule, anywhere in the U.S.
              </p>

              <div className="plh-actions">
                <Link to="/login" className="plh-gold" style={{ background: T.gold }}>
                  Get Started as a Driver <Icon name="arrow" size={18} stroke={2.6} />
                </Link>
                <Link to="/company-login" className="plh-company">
                  <Icon name="building" size={18} /> Post a Load as a Company
                </Link>
              </div>
            </div>
          </div>

          {/* Photo */}
          <div className="plh-photo">
            <picture>
              {/* Tiny blank source on small screens so phones never download the photo */}
              <source media="(max-width: 1023px)" srcSet="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" />
            <img
                src={heroTruck}
                alt="Pilot car leading an oversize load truck down the highway"
                className="plh-img"
                style={{ filter: "saturate(1.06) contrast(1.04)" }}
              />
            </picture>
            <div aria-hidden="true" className="plh-mscrim" />

          </div>

          {/* Action card */}
          <button type="button" onClick={handleLoadBoard} className="plh-float" style={{
            background: T.glass, border: `1px solid ${T.border}`, color: T.text,
          }}>
            <span className="plh-float-icon" style={{ background: T.accentBg, color: accentText }}>
              <Icon name="clipboard" size={20} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12.5, color: T.textMid }}>Need an escort fast?</span>
              <span style={{ display: "block", fontSize: 16, fontWeight: 800, color: accentText, margin: "1px 0" }}>Browse open loads</span>
              <span style={{ display: "block", fontSize: 12, color: T.textSoft }}>Updated in real time</span>
            </span>
            <span style={{ color: T.textMid }}><Icon name="arrow" size={18} /></span>
          </button>
        </div>

        <div className="plh-inner plh-bottom">
          {/* Feature cards */}
          <div className="plh-features">
            {[
              ["shield",    "Verified Drivers",    "ID, certification and insurance checked before every job."],
              ["pin",       "Nationwide Coverage", "Certified escort drivers across all 50 states."],
              ["clipboard", "Live Load Board",     "Real-time oversize loads with rates and permit details."],
              ["file",      "Training & Permits",  "State-by-state certification courses and requirements."],
            ].map(([icon, title, desc]) => (
              <div key={title} className="plh-feature" style={{
                background: T.surface, border: `1px solid ${T.border}`,
                boxShadow: isDarkMode ? "0 14px 36px rgba(0,0,0,0.28)" : "0 14px 36px rgba(15,23,42,0.07)",
              }}>
                <span className="plh-feature-icon" style={{ background: T.accentBg, color: accentText }}>
                  <Icon name={icon} size={22} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 15.5, fontWeight: 800, color: T.text, marginBottom: 3 }}>{title}</span>
                  <span style={{ display: "block", fontSize: 13.5, color: T.textMid, lineHeight: 1.5 }}>{desc}</span>
                </span>
              </div>
            ))}
          </div>

          {/* Stats strip */}
          <div className="plh-stats" style={{ borderTop: `1px solid ${T.border}` }}>
            {[
              ["shield", "50",     "States Covered"],
              ["truck",  "7 Days", "Free Trial"],
              ["users",  "100%",   "Drivers Verified"],
              ["chat",   "24/7",   "Support"],
            ].map(([icon, v, l]) => (
              <div key={l} className="plh-stat">
                <span style={{ color: isDarkMode ? T.accentL : NAVY }}><Icon name={icon} size={30} stroke={1.8} /></span>
                <span style={{ minWidth: 0 }}>
                  <span className="plh-stat-value" style={{ color: accentText }}>{v}</span>
                  <span style={{ display: "block", fontSize: 13.5, color: T.textMid }}>{l}</span>
                </span>
              </div>
            ))}
            <div className="plh-map-tag">
              <svg viewBox="0 0 959 593" aria-hidden="true" className="plh-map">
                {Object.entries(STATE_PATHS).map(([code, d]) => (
                  <path key={code} d={d} fill={isDarkMode ? "#2a4268" : "#cbd5e1"} stroke={T.bg} strokeWidth={2} />
                ))}
              </svg>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".22em", lineHeight: 1.7, textTransform: "uppercase", color: isDarkMode ? T.text : NAVY }}>
                Keeping America<br />
                <span style={{ color: accentText }}>in motion</span>
                <span style={{ display: "block", width: 80, height: 2, background: T.gold, borderRadius: 2, marginTop: 6 }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ PARTNER PROGRAM ═══════════ */}
      <PartnerSection T={T} isDarkMode={isDarkMode} />

      {/* ══════════ WHAT WE DO — at a glance ══════════ */}
      <div style={{
        background: isDarkMode ? "#081427" : NAVY,
        borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
        padding: "16px clamp(16px, 3vw, 32px)",
        display: "flex", justifyContent: "center", alignItems: "center",
        gap: "8px clamp(12px, 2.4vw, 26px)", flexWrap: "wrap",
      }}>
        {tickerItems.map((item, i) => (
          <React.Fragment key={item}>
            <span style={{
              fontSize: "clamp(12px, 1.4vw, 14px)", fontWeight: 700,
              color: "rgba(241,245,249,0.88)",
              fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: "nowrap",
            }}>{item}</span>
            {i < tickerItems.length - 1 && (
              <span aria-hidden="true" style={{ width: 4, height: 4, borderRadius: "50%", background: "#60a5fa" }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ══════════════════ SERVICES ══════════════════ */}
      <section style={{ background: T.bg, padding: "clamp(56px, 8vw, 96px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "clamp(34px, 6vw, 52px)" }}>
            <Eyebrow T={T} isDarkMode={isDarkMode} center>Everything we offer</Eyebrow>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px, 3.4vw + 10px, 40px)", fontWeight: 800, color: T.text, letterSpacing: "-0.02em", margin: 0 }}>
              All your escort needs. One login.
            </h2>
            <p style={{ color: T.textMid, fontSize: "clamp(15px, 1.8vw, 17px)", maxWidth: 560, margin: "14px auto 0", lineHeight: 1.65 }}>
              Stop stitching together job boards, group chats and spreadsheets. PilotLink USA puts the whole escort workflow on one platform.
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
            gap: "clamp(16px, 2.4vw, 22px)",
          }}>
            {services.map(s => (
              <Card T={T} key={s.title} style={{ padding: "clamp(22px, 3vw, 30px)", cursor: "pointer" }} to={`/${s.page}`}>
                <div style={{ marginBottom: 18 }}><IconTile T={T} name={s.icon} /></div>
                <div style={{ fontWeight: 700, fontSize: "clamp(16px, 2vw, 18px)", color: T.text, marginBottom: 8 }}>{s.title}</div>
                <div style={{ fontSize: 14, color: T.textMid, lineHeight: 1.65, marginBottom: 18 }}>{s.desc}</div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, color: accentText, fontWeight: 700 }}>
                  Explore <Icon name="arrow" size={15} stroke={2.4} />
                </span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ WHY US ═══════════════ */}
      <section style={{
        background: isDarkMode ? T.surface : T.surface2,
        borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
        padding: "clamp(56px, 8vw, 96px) clamp(20px, 4vw, 40px)",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
            gap: "clamp(32px, 5vw, 64px)", alignItems: "center",
          }}>
            <div>
              <Eyebrow T={T} isDarkMode={isDarkMode}>Why PilotLink USA</Eyebrow>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(28px, 3.4vw + 8px, 38px)", fontWeight: 800, color: T.text, lineHeight: 1.15, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
                The only platform built for the whole escort industry
              </h2>
              <p style={{ color: T.textMid, fontSize: "clamp(15px, 1.8vw, 17px)", lineHeight: 1.75, marginBottom: 24 }}>
                Other sites list jobs. We run the industry's full workflow and verify everyone on it. Companies book with confidence because every driver's identity, certifications and insurance are checked before they take a load.
              </p>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 12,
                background: T.greenBg, border: `1px solid ${T.green}44`,
                borderRadius: 12, padding: "12px 16px", color: T.green,
              }}>
                <Icon name="shield" size={20} />
                <span style={{ fontSize: 14, fontWeight: 700 }}>
                  Verified Driver Guarantee: every profile checked, every cert confirmed
                </span>
              </div>
            </div>

            <div style={{ display: "grid", gap: "clamp(12px, 2vw, 14px)" }}>
              {trustPillars.map(p => (
                <Card T={T} key={p.title} style={{ padding: "clamp(18px, 2.5vw, 22px)", display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <IconTile T={T} name={p.icon} size={42} iconSize={20} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15.5, color: T.text, marginBottom: 4 }}>{p.title}</div>
                    <div style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.6 }}>{p.desc}</div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════ STATE COVERAGE MAP ══════════════════════════ */}
      <section style={{ background: T.bg, padding: "clamp(48px, 7vw, 88px) clamp(20px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "clamp(26px, 4vw, 36px)" }}>
            <Eyebrow T={T} isDarkMode={isDarkMode} center>Nationwide coverage</Eyebrow>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(26px, 3vw + 8px, 34px)", fontWeight: 800, color: T.text, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
              Find your state's requirements
            </h2>
            <p style={{ color: T.textMid, fontSize: 15, maxWidth: 520, margin: "0 auto" }}>
              Click any state to see its certification requirements and enroll in the right course.
            </p>
          </div>
          <HomeStateMap T={T} isDarkMode={isDarkMode} />
        </div>
      </section>

      {/* ═══════════════════════ CTA FOOTER ═══════════════════════ */}
      <section style={{
        position: "relative", overflow: "hidden", isolation: "isolate",
        background: `linear-gradient(160deg, ${NAVY} 0%, #173058 60%, ${NAVY} 100%)`,
        padding: "clamp(64px, 10vw, 120px) clamp(20px, 4vw, 40px)",
        textAlign: "center",
      }}>
        <div aria-hidden="true" style={{
          position: "absolute", inset: 0, zIndex: -1,
          backgroundImage: "radial-gradient(rgba(241,245,249,0.10) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 40%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 40%, black 40%, transparent 100%)",
          opacity: 0.5,
        }} />
        <div aria-hidden="true" style={{
          position: "absolute", bottom: "-15%", right: "6%", zIndex: -1,
          width: "min(460px, 55vw)", height: "min(460px, 55vw)", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(59,130,246,0.16) 0%, transparent 70%)",
        }} />

        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <h2 style={{
            fontFamily: "'Plus Jakarta Sans',sans-serif",
            fontSize: "clamp(32px, 4.4vw + 14px, 52px)",
            fontWeight: 900, lineHeight: 1.1, color: "#f1f5f9",
            margin: "0 0 clamp(18px, 3vw, 24px)", letterSpacing: "-0.03em",
          }}>
            One platform for every escort job in America
          </h2>

          <p style={{
            color: "rgba(241,245,249,0.75)", fontSize: "clamp(16px, 1.4vw + 8px, 19px)",
            maxWidth: 560, margin: "0 auto clamp(32px, 5vw, 44px)", lineHeight: 1.75,
          }}>
            Get certified, get verified, get matched with loads and get paid, all in one place. Companies post in minutes; drivers apply in seconds.
          </p>

          <div style={{
            display: "flex", gap: "clamp(12px, 2vw, 16px)", justifyContent: "center",
            flexWrap: "wrap", marginBottom: "clamp(24px, 4vw, 32px)",
          }}>
            <PrimaryBtn T={T} to="/training" style={{ fontSize: "clamp(15px, 1.6vw, 17px)", padding: "16px 34px", flex: "0 1 auto" }}>
              Start free trial <Icon name="arrow" size={18} stroke={2.4} />
            </PrimaryBtn>
            <SecondaryBtn T={T} onNavy onClick={handleLoadBoard} style={{ fontSize: "clamp(15px, 1.6vw, 17px)", padding: "16px 34px", flex: "0 1 auto" }}>
              <Icon name="clipboard" size={18} /> Browse open loads
            </SecondaryBtn>
          </div>

          <div style={{
            display: "flex", gap: "clamp(16px, 3vw, 28px)", justifyContent: "center",
            flexWrap: "wrap", fontSize: 13.5, color: "rgba(241,245,249,0.6)", fontWeight: 600,
          }}>
            {["7-day free trial", "Cancel anytime"].map((t) => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ color: "#34d399" }}><Icon name="check" size={15} stroke={3} /></span>{t}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
